<template>
  <div class="page-content">
    <div class="headings">
      <h2 class="title title-primary">{{ location }}</h2>
      <span>|</span>
      <h2 class="title title-primary">
        {{ $t("lang.inventory.dashboard.page_title_line_1") }}
      </h2>
      <span>|</span>
      <h3 class="title title-primary">
        {{ $t("lang.inventory.dashboard.page_title_line_2") }}
      </h3>
    </div>
    <div class="page-header">
      <div class="boxes" v-if="!shouldLoading">
        <bx-box dark color="outline" class="col-12 col-md-auto p-1">
          <bx-value
            :label="$t('lang.inventory.dashboard.box_label_3')"
            :value="stats.total_inventory_count"
            font-size="xs"
            :icon="require('@/assets/i/boxWhiteBig.png')"
          />
        </bx-box>

        <bx-box dark color="outline" class="col-6 col-md-auto p-1">
          <bx-value
            :label="$t('lang.inventory.dashboard.box_label_1')"
            :value="stats.pending_inventories"
            font-size="xs"
          />
        </bx-box>

        <bx-box dark color="outline" class="col-6 col-md-auto p-1">
          <bx-value
            :label="$t('lang.inventory.dashboard.box_label_2')"
            :value="stats.validated_inventories"
            font-size="xs"
          />
        </bx-box>
        <bx-box dark color="outline" class="col-6 col-md-auto p-1">
          <bx-value
            :label="$t('lang.inventory.dashboard.box_label_6')"
            :value="stats.estimated_inventory_time"
            font-size="xs"
          />
        </bx-box>
        <bx-box dark color="outline" class="col-6 col-md-auto p-1">
          <bx-value
            :label="$t('lang.inventory.dashboard.box_label_5')"
            :value="stats.avg_inventory_time"
            font-size="xs"
          />
        </bx-box>
        <bx-box dark color="outline" class="col-6 col-md-auto p-1">
          <bx-value
            :label="$t('lang.inventory.dashboard.box_label_4')"
            :value="stats.avg_inventory_items"
            font-size="xs"
          />
        </bx-box>
        <bx-box dark color="outline" class="col-6 col-md-auto p-1">
          <bx-value
            :label="$t('lang.inventory.dashboard.box_label_7')"
            :value="stats.avg_inventory_accuracy"
            font-size="xs"
          />
        </bx-box>
      </div>
      <skeleton-boxes :boxes="7" v-else />
    </div>
    <div class="page-main-content">
      <div class="container-fluid">
        <div class="row">
          <div class="col-12">
            <bx-box
              :title="$t('lang.common.historic')"
              no-padding
              :loading="shouldLoading"
            />
            <div class="form-row d-flex align-items-end">
              <div class="select-box col-md-4">
                <v-select
                  v-model="areaSelected"
                  :items="areaList"
                  item-value="id"
                  item-text="description"
                  v-on:change="getData"
                  :label="$t('lang.admin.device_form.label_select_location')"
                  outlined
                  v-if="!shouldLoading"
                ></v-select>
              </div>
              <div class="select-box col-md-4">
                <v-select
                  v-model="state"
                  :items="filterState"
                  item-value="value"
                  v-on:change="getData"
                  :label="$t('lang.common.state')"
                  outlined
                  v-if="!shouldLoading"
                ></v-select>
              </div>
              <div class="select-box col-md-4">
                <v-select
                  v-model="type"
                  :items="filterType"
                  item-value="value"
                  v-on:change="getData"
                  :label="$t('lang.common.type')"
                  outlined
                  v-if="!shouldLoading"
                ></v-select>
              </div>
            </div>
            <bx-table
              :data="data"
              :columns="columns"
              paginated
              v-if="!shouldLoading"
              backendPagination
              :currentPage="page"
              :perPage="itemsPerPage"
              :total="ordersCount"
              @search="onSearch"
              @update:currentPage="updatePage"
            >
              <template slot-scope="props">
                <bx-table-column field="status" centered>
                  <img
                    v-if="props.row.is_partial"
                    style="cursor: pointer ; width: 20px; height: auto"
                    src="@/assets/i/p-grey.svg"
                  />
                  <img
                    v-else
                    style="cursor: pointer; width: 20px; height: auto"
                    src="@/assets/i/t-green.svg"
                  />
                </bx-table-column>
                <bx-table-column field="status" centered>
                  <bx-tag v-if="props.row.state === 3" color="red">
                    {{ $t("lang.inventory.box_status.label_canceled") }}
                  </bx-tag>
                  <bx-tag v-else-if="props.row.state === 1" color="blue">
                    {{ $t("lang.inventory.box_status.label_computing") }}
                  </bx-tag>
                  <bx-tag v-else-if="props.row.state === 2" color="lime">
                    {{ $t("lang.inventory.box_status.label_validated") }}
                  </bx-tag>
                  <bx-tag v-else color="yellow">
                    {{ $t("lang.inventory.box_status.label_pending") }}
                  </bx-tag>
                </bx-table-column>
                <bx-table-column centered field="id">
                  <router-link
                    :to="`/inventory/${props.row.id}/detail`"
                    class="bx-link bx-link-bold"
                    >{{ props.row.id }}</router-link
                  >
                </bx-table-column>
                <bx-table-column field="date">
                  {{ formatDate(props.row.start_date) }}
                </bx-table-column>
                <bx-table-column field="end_date">
                  {{ formatDate(props.row.end_date) }}
                </bx-table-column>
                <bx-table-column centered>
                  {{ props.row.theorical }}
                </bx-table-column>
                <bx-table-column centered>
                  {{ props.row.real }}
                </bx-table-column>
                <bx-table-column>
                  <v-icon
                    @click="viewDetail(props.row.id)"
                    dense
                    color="primary"
                    dark
                  >
                    mdi-help-circle
                  </v-icon>
                  <v-icon
                    @click="downloadFile(props.row.id)"
                    dense
                    color="#212428"
                    dark
                  >
                    mdi-arrow-down-bold-circle-outline
                  </v-icon>
                </bx-table-column>
              </template>
            </bx-table>
          </div>
        </div>
        <v-row class="d-flex d-sm-none">
          <div
            v-infinite-scroll="loadMore"
            infinite-scroll-disabled="busy"
            infinite-scroll-distance="20"
            style="width: 100%"
          >
            <v-list width="100%">
              <v-list-item
                two-line
                v-for="(item, index) in dataList"
                :key="index"
                class="green lighten-5 ma-2 rounded-lg"
                ripple
                :to="`/inventory/${item.id}/detail`"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <div>
                      {{ item.id }}
                    </div>
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-list-item-subtitle class="text-caption">
                    {{ item.real + "/" + item.theorical }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption">
                    {{ formatDate(item.date) }}
                  </v-list-item-subtitle>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </v-row>
        <skeleton-table :columns="6" v-if="shouldLoading" />
      </div>
    </div>
    <v-dialog v-model="confirmDialog" persistent scrollable width="600px">
      <v-card>
        <v-card-title class="text-h5 font-weight-bold">
          {{ $t("lang.inventory.download.title") }}
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text>
          <div>
            <p>{{ $t("lang.inventory.download.message") }}</p>

            <!-- Centering the button using v-row and v-col -->
            <v-row justify="center">
              <v-col cols="auto">
                <v-btn 
                  color="primary" 
                  class="mt-3" 
                  @click="closeDialog"
                >
                  {{ $t("lang.inventory.download.close") }}
                </v-btn>
              </v-col>
            </v-row>
            
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
let filterState = [
  { value: null, text: "Todos" },
  { value: 0, text: "Pendientes" },
  { value: 1, text: "Computando" },
  { value: 2, text: "Validados" },
  { value: 3, text: "Cancelados" }
];

let filterType = [
  { value: null, text: "Todos" },
  { value: true, text: "Totales" },
  { value: false, text: "Parciales" }
];

import moment from "moment";
import common from "@/utils/common";

export default {
  name: "inventory-dashboard",
  mixins: [common],
  components: {},
  data() {
    return {
      shouldLoading: true,
      progress: "0",
      confirmDialog: false,
      lastProgress: 0,
      reloadInterval: null,
      ordersCount: 1,
      columns: [],
      data: [],
      dataList: [],
      areaList: [],
      areaDefault: [{ id: null, description: "Todas" }],
      areaSelected: null,
      pending: 0,
      filterState: filterState,
      filterType: filterType,
      state: null,
      type: null,
      completed: 0,
      total: 0,
      stats: {},
      page: 1,
      itemsPerPage: process.env.VUE_APP_ITEMS_PER_PAGE
    };
  },
  computed: {
    location() {
      return localStorage.getItem("location");
    }
  },
  methods: {
    loadMore() {
      if (
        !this.shouldLoading &&
        this.dataList.length &&
        this.dataList.length < this.ordersCount &&
        this.isMobile
      ) {
        this.page++;
        this.getData(this.locationId);
      }
    },
    closeDialog() {
      this.confirmDialog = false;
    },
    formatDate(date) {
      return date
        ? moment(date).format("DD/ MM/ YYYY, h:mm a")
        : this.$t("lang.messages.no_date_available");
    },
    onSearch(field, value) {
      this.filters[field] = value;
      this.getData();
    },
    updatePage(p) {
      this.page = p;
      this.getData(this.locationId);
    },
    handleProgress(progress) {
      this.progress = progress;
      if (progress == 100) {
        this.confirmDialog = false;
        this.progress = 0;
      }
      this.lastProgress = progress;
    },
    async downloadFile(id) {
      try {
        if (this.dialog) return;
        this.confirmDialog = true;
        await this.$api.inventories.download(id);
        this.confirmDialog = false;
      } catch (error) {
        console.log(error);
      }
    },
    viewDetail(id) {
      this.$router.push("/inventory/" + id + "/detail-items");
    },
    async getData() {
      try {
        if (this.isMobile) {
          this.shouldLoading = true;
        }
        // Load stats
        let location = localStorage.getItem("location");
        let locationId = localStorage.getItem("location_id");
        if (!location || !locationId) {
          try {
            let firstLocation = await this.$api.location.search("", {
              limit: 1,
              offset: 0,
              orderBy: "id"
            });
            location = firstLocation.results[0].description;
            locationId = firstLocation.results[0].id;
          } catch (error) {
            console.log(error);
          }
        }

        this.areaList = await this.$api.area.locationAreaList(locationId);
        this.areaList = this.areaDefault.concat(this.areaList);
        this.stats = await this.$api.location.inventoryStats(locationId);

        // Load inventories
        const inventories = await this.$api.inventories.list(locationId, {
          offset: (this.page - 1) * this.itemsPerPage,
          limit: this.itemsPerPage,
          state: this.state,
          location_area: this.areaSelected,
          inventorypartial__isnull: this.type,
          order_by: "-id"
        });
        this.ordersCount = inventories.count;
        this.data = inventories.results.map(item => {
          return {
            id: item.id,
            valid: item.valid,
            state: item.state,
            start_date: item.start_time,
            end_date: item.end_time,
            stockRatingPercent:
              item.theorical_stock < item.matched_items
                ? (item.theorical_stock / item.matched_items) * 100
                : (item.matched_items / item.theorical_stock) * 100,
            ismore: item.theorical_stock < item.matched_items,
            real: item.items,
            theorical: item.theorical_stock,
            is_partial: item.is_partial,
            canceled: item.canceled
          };
        });

        if (this.isMobile) {
          this.dataList = this.dataList.concat(this.data);
        }

        this.columns = [
          {
            field: "type",
            label: this.$t("lang.common.type"),
            width: "120",
            centered: true
          },
          {
            field: "status",
            label: this.$t("lang.inventory.dashboard.table_field_1"),
            width: "120",
            centered: true
          },
          {
            field: "id",
            label: this.$t("lang.inventory.dashboard.table_field_2"),
            width: "120",
            centered: true
          },
          {
            field: "date",
            label: this.$t("lang.inventory.dashboard.table_field_3")
          },
          {
            field: "end_date",
            label: this.$t("lang.inventory.dashboard.table_field_31")
          },
          {
            field: "theorical",
            label: this.$t("lang.inventory.dashboard.table_field_5"),
            centered: true
          },
          {
            field: "real",
            label: this.$t("lang.inventory.dashboard.table_field_6"),
            centered: true
          },
          {
            field: "detail",
            label: "Detalle",
            centered: true
          }
        ];
      } catch (error) {
        // console.log(error)
      } finally {
        this.shouldLoading = false;
      }
    }
  },
  mounted() {
    if (!this.isMobile) {
      this.reloadInterval = setInterval(
        this.getData,
        process.env.VUE_APP_RELOAD_TIME
      );
    }
    this.getData();
  },
  beforeDestroy() {
    clearInterval(this.reloadInterval);
  }
};
</script>
